import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  Divider,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useChangePasswordMutation } from "../../../api/authApi";
import { useFormSubmission } from "../../../hooks/useFormSubmission";

const validationSchema = Yup.object({
  currentPassword: Yup.string()
    .required("Current password is required"),
  newPassword: Yup.string()
    .min(6, "New password must be at least 6 characters long")
    .max(100, "New password cannot exceed 100 characters")
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "New password and confirm password do not match")
    .required("Confirm password is required"),
});

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePasswordTab: React.FC = () => {
  const navigate = useNavigate();
  const [changePassword] = useChangePasswordMutation();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    isSubmitting,
    submitError,
    successMessage,
    handleSubmit,
    clearMessages,
  } = useFormSubmission({
    onSuccess: () => {
      // Success message will be shown in the form
    },
    successMessage: "Your password has been changed successfully!",
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleSubmit(async () => {
        await changePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        }).unwrap();
      });
    },
  });

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" component="h2" gutterBottom color="text.primary">
          Change Password
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Update your password to keep your account secure. Make sure to use a strong password.
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submitError}
        </Alert>
      )}

      {/* Form */}
      <Box component="form" onSubmit={formik.handleSubmit} sx={{ maxWidth: 500 }}>
        {/* Current Password */}
        <Box sx={{ position: "relative", mb: 3 }}>
          <TextField
            name="currentPassword"
            label="Current Password"
            type={showCurrentPassword ? "text" : "password"}
            required
            size="medium"
            placeholder="Enter your current password"
            fullWidth
            value={formik.values.currentPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.currentPassword && Boolean(formik.errors.currentPassword)}
            helperText={formik.touched.currentPassword && formik.errors.currentPassword}
          />
          <Button
            size="small"
            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
            sx={{ 
              position: "absolute", 
              right: 8, 
              top: "50%", 
              transform: "translateY(-50%)",
              minWidth: "auto", 
              px: 1,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
                color: "text.primary"
              }
            }}
          >
            {showCurrentPassword ? "Hide" : "Show"}
          </Button>
        </Box>

        {/* New Password */}
        <Box sx={{ position: "relative", mb: 3 }}>
          <TextField
            name="newPassword"
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            required
            size="medium"
            placeholder="Enter your new password"
            fullWidth
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
            helperText={formik.touched.newPassword && formik.errors.newPassword}
          />
          <Button
            size="small"
            onClick={() => setShowNewPassword(!showNewPassword)}
            sx={{ 
              position: "absolute", 
              right: 8, 
              top: "50%", 
              transform: "translateY(-50%)",
              minWidth: "auto", 
              px: 1,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
                color: "text.primary"
              }
            }}
          >
            {showNewPassword ? "Hide" : "Show"}
          </Button>
        </Box>

        {/* Confirm Password */}
        <Box sx={{ position: "relative", mb: 4 }}>
          <TextField
            name="confirmPassword"
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            required
            size="medium"
            placeholder="Confirm your new password"
            fullWidth
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
          />
          <Button
            size="small"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            sx={{ 
              position: "absolute", 
              right: 8, 
              top: "50%", 
              transform: "translateY(-50%)",
              minWidth: "auto", 
              px: 1,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
                color: "text.primary"
              }
            }}
          >
            {showConfirmPassword ? "Hide" : "Show"}
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-start" }}>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !formik.isValid}
            sx={{ minWidth: 140 }}
          >
            {isSubmitting ? "Changing..." : "Change Password"}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              formik.resetForm();
              clearMessages();
            }}
            disabled={isSubmitting}
          >
            Reset
          </Button>
        </Box>
      </Box>

      {/* Security Tips */}
      <Box sx={{ 
        mt: 4, 
        p: 2, 
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: 1
      }}>
        <Typography variant="subtitle2" gutterBottom color="text.primary">
          Password Security Tips:
        </Typography>
        <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, m: 0 }}>
          <li>Use at least 8 characters</li>
          <li>Include uppercase and lowercase letters</li>
          <li>Include numbers and special characters</li>
          <li>Don't use personal information</li>
          <li>Don't reuse passwords from other accounts</li>
        </Typography>
      </Box>
    </Box>
  );
};

export default ChangePasswordTab;
